/**
 * Market Radar display primitives.
 *
 * The single most important rule here: an unmeasured value is rendered as a
 * visible "n/a", never as 0 and never as a blank cell. A SCAN-tier symbol has
 * no trade or book feed, so those metrics are absent — that is a statement
 * about our observation, not about the market. Blanking or zeroing them would
 * make quiet-looking data indistinguishable from unobserved data, and would
 * make SCAN symbols look worse than they are.
 */

import React from "react";
import type { AttentionTier, LifecycleState, RadarStatus } from "@/hooks/useMarketRadar";

export const COLORS = {
  accent: "#ff9100",
  text: "#f0f0f0",
  dim: "#888",
  fainter: "#555",
  border: "#1a1a1a",
  good: "#00cc66",
  bad: "#ff4444",
  warn: "#ff9100",
};

/** Rendered wherever a metric was not measured. */
export function NotMeasured({ title }: { title?: string }) {
  return (
    <span
      title={title ?? "not measured at this data quality"}
      style={{ color: "#3a3a3a", fontStyle: "italic" }}
      data-testid="not-measured"
    >
      n/a
    </span>
  );
}

/**
 * Format a number, or mark it as unmeasured.
 *
 * `0` is a real measurement and prints as `0`; only `null`/`undefined` become
 * "n/a".
 */
export function num(
  value: number | null | undefined,
  digits = 2,
  suffix = "",
): React.ReactNode {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <NotMeasured />;
  }
  return `${value.toFixed(digits)}${suffix}`;
}

/** Compact turnover: 1.2B / 340M / 12K. */
export function turnover(value: number | null | undefined): React.ReactNode {
  if (value === null || value === undefined || !Number.isFinite(value)) return <NotMeasured />;
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toFixed(0);
}

/**
 * Last traded price, at a precision that suits the symbol.
 *
 * Fixed decimals cannot serve this column. Perp quotes on this board span
 * roughly 0.000001 to 100000, so `toFixed(2)` collapses every low-priced
 * symbol to "0.00" and `toFixed(8)` makes BTC unreadable. The tiers below
 * keep about five significant digits at every magnitude, and stay tiers
 * rather than `toPrecision` so decimal points still line up within the
 * band a reader is actually scanning.
 *
 * A non-positive price is treated as absent, not as zero: the exchange does
 * not quote a live contract at zero, so such a value is missing data and must
 * never render as a real price.
 */
export function price(value: number | null | undefined): React.ReactNode {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return <NotMeasured />;
  }
  if (value >= 1_000) return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (value >= 1) return value.toFixed(4);
  if (value >= 0.01) return value.toFixed(5);
  if (value >= 0.0001) return value.toFixed(6);
  return value.toFixed(8);
}

export function ageLabel(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "n/a";
  if (ms < 1_000) return "now";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * Lifecycle colours.
 *
 * Rising states are warm, decaying and dormant are muted, so the direction of
 * a market reads at a glance without reading the label.
 */
export const LIFECYCLE_COLOR: Record<LifecycleState, string> = {
  ACCELERATING: "#00cc66",
  ACTIVE: "#ff9100",
  PEAK: "#ffd166",
  DECAYING: "#c86464",
  EMERGING: "#66aaff",
  DORMANT: "#555",
};

export function LifecycleBadge({ state }: { state: LifecycleState }) {
  const color = LIFECYCLE_COLOR[state] ?? COLORS.fainter;
  return (
    <span
      data-testid={`lifecycle-${state}`}
      style={{
        color,
        border: `1px solid ${color}`,
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: "0.08em",
        padding: "1px 4px",
        whiteSpace: "nowrap",
      }}
    >
      {state}
    </span>
  );
}

/** Attention tier: how closely the radar is watching. Filled = deeper. */
export function TierBadge({ tier }: { tier: AttentionTier }) {
  const style: Record<AttentionTier, React.CSSProperties> = {
    deep: { background: COLORS.accent, color: "#000", border: `1px solid ${COLORS.accent}` },
    watch: { color: COLORS.accent, border: `1px solid ${COLORS.accent}` },
    scan: { color: COLORS.fainter, border: "1px solid #333" },
  };
  return (
    <span
      data-testid={`tier-${tier}`}
      title={
        tier === "deep" ? "DEEP: ticker + trades + order book"
          : tier === "watch" ? "WATCH: ticker + trades"
            : "SCAN: REST ticker polling only"
      }
      style={{ ...style[tier], fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", padding: "1px 4px" }}
    >
      {tier.toUpperCase()}
    </span>
  );
}

/**
 * Data quality: which feeds actually backed these numbers.
 *
 * Deliberately separate from the tier badge. A symbol promoted a moment ago is
 * WATCH tier but still has only SCAN-quality data until its buckets fill, and
 * that gap is exactly what explains missing metrics.
 */
export function DataQualityBadge({ quality }: { quality: AttentionTier }) {
  const label = { scan: "SCAN", watch: "WATCH", deep: "DEEP" }[quality];
  const explain = {
    scan: "Backed by REST ticker polling only. Trade rate, aggressor flow, book imbalance and depth are not measured for this symbol.",
    watch: "Backed by ticker + trade websocket. Book depth and imbalance are not measured.",
    deep: "Backed by ticker + trades + order book. All metrics measured.",
  }[quality];
  return (
    <span
      data-testid={`dq-${quality}`}
      title={explain}
      style={{ color: COLORS.dim, fontSize: 9, letterSpacing: "0.06em", borderBottom: "1px dotted #333", cursor: "help" }}
    >
      {label}
    </span>
  );
}

/** Radar freshness. Never let a stale board look live. */
export function RadarStatusBadge({ status, ageMs }: { status: RadarStatus; ageMs: number | null }) {
  const map: Record<RadarStatus, { color: string; label: string; title: string }> = {
    live: { color: COLORS.good, label: "LIVE", title: "Current market state from the running worker" },
    warming: {
      color: "#66aaff",
      label: "WARMING",
      title: "The radar is still accumulating baseline history. Rankings are not yet reliable.",
    },
    stale: {
      color: COLORS.warn,
      label: "STALE",
      title: "Not current market state — served from the last stored snapshot.",
    },
    unavailable: { color: COLORS.bad, label: "UNAVAILABLE", title: "No radar snapshot is available." },
  };
  const entry = map[status] ?? map.unavailable;
  return (
    <span
      data-testid={`radar-status-${status}`}
      title={entry.title}
      style={{
        color: entry.color,
        border: `1px solid ${entry.color}`,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.1em",
        padding: "2px 6px",
        whiteSpace: "nowrap",
      }}
    >
      {entry.label}
      {ageMs !== null && <span style={{ color: COLORS.dim, marginLeft: 6, fontWeight: 600 }}>{ageLabel(ageMs)}</span>}
    </span>
  );
}

/**
 * Executability, kept visually distinct from activity.
 *
 * Activity is "is this market moving"; executability is "could that movement
 * be acted on". They are separate answers and must not be read as one score.
 */
export function ExecutabilityCell({
  score,
  executable,
  reasons,
}: {
  score: number | null;
  executable: boolean | null;
  reasons: string[];
}) {
  if (executable === null || score === null) return <NotMeasured title="liquidity not measured" />;
  return (
    <span
      title={reasons.length > 0 ? reasons.join("; ") : "clears the executability floor"}
      style={{ color: executable ? COLORS.good : COLORS.dim, fontWeight: 700 }}
    >
      {executable ? "YES" : "NO"}
      <span style={{ color: COLORS.fainter, fontWeight: 500, marginLeft: 4 }}>{score.toFixed(2)}</span>
    </span>
  );
}

/** Rank movement: positive means the symbol climbed. */
export function MomentumCell({ rank, previousRank }: { rank: number | null; previousRank: number | null }) {
  if (rank === null || previousRank === null) return <NotMeasured title="no previous rank yet" />;
  const momentum = previousRank - rank;
  if (momentum === 0) return <span style={{ color: COLORS.fainter }}>0</span>;
  const rising = momentum > 0;
  return (
    <span style={{ color: rising ? COLORS.good : "#c86464", fontWeight: 700 }}>
      {rising ? "▲" : "▼"} {Math.abs(momentum)}
    </span>
  );
}
